import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { authReady } from 'rp-navigation';
import { PagerDefault as Pager } from 'rp-react-pagination';
import PriceRangeFilter from '../../modules/PriceRangeFilter';
import { setUrl } from '../../actions/gridDataTable';
import * as catalogueActions from '../../actions/catalogue';
import Refinement from './Refinement';
import upcomingConfig from '../../config/upcomingSalesTable';
import resultsConfig from '../../config/salesResultsTable';
import { expandRow } from '../../actions/expandableRow';
import UpcomingFooterText from '../../components/UpcomingFooterText';
import CatalogueTable from './CatalogueTable';
import {
    updatePagerAndLotsFound,
    updateTableOnPageChange,
} from '../../helpers/dispatchProps';
import { queryUrl } from '../../helpers/urlBuilder';
import {
    catalogueStateKey,
    isCommonFiltersTabDisabled,
    refinementSelector,
    cataloguePagerLength,
    isCatalogueUpcoming,
} from '../../helpers/catalogueUtils';
import FiltersLabel from './FiltersLabel';
import RelativesLightbox from '../../components/Lightbox';

const upcoming = isCatalogueUpcoming();
const tableConfig = upcoming ? upcomingConfig : resultsConfig;

/**
 * Container for sales catalogue view
 */
class Catalogue extends PureComponent {
    static propTypes = {
        refinement: PropTypes.object,
        applySearchPriceMinFilter: PropTypes.func.isRequired,
        applySearchPriceMaxFilter: PropTypes.func.isRequired,
        /**
         * DataTable onAfterUpdate event handler
         * Dispatch prop
         */
        onAfterUpdate: PropTypes.func,
        /**
         * Pager onPageChange event handler
         * Dispatch prop
         */
        onPageChange: PropTypes.func,
        /**
         * Expandable row onRowExpand event handler
         * Dispatch prop
         */
        onRowExpand: PropTypes.func,
        /**
         * GridDataTable setUrl action
         * Dispatch prop
         */
        setUrl: PropTypes.func,
        /**
         * Check for sorting to enable / disable page ranges
         * State prop
         */
        currency: PropTypes.string,

        data: PropTypes.object,

        pagerCustomLabels: PropTypes.arrayOf(PropTypes.shape({
            page: PropTypes.number,
            label: PropTypes.string,
        })),
    };

    constructor(props) {
        super(props);

        if (tableConfig.activeLot) {
            this.activeLot = 1 * (tableConfig.activeLot);
            this.props.setUrl(`${tableConfig.url}?page=${tableConfig.initialPage}`);
            this.props.onRowExpand({
                lot_no: this.activeLot,
                lot_letter: tableConfig.lotLetter,
                venue_uid: tableConfig.venueUid
            });
        }

        this.state = {
            lightbox: false
        };
    }

    componentDidMount() {
        const myShortlist = document.querySelectorAll('.js-myShortlistTab');
        const tabMore = document.querySelector('.js-tabs__tab_more');
        return authReady.then((auth) => {
            if (auth.user.isLoggedIn) {
                return;
            }
            Array.from(myShortlist).forEach((el) => {
                el.addEventListener('click', (e) => {
                    e.preventDefault();
                    tabMore.click();
                    this.setState({
                        lightbox: true,
                    });
                });
            });
        });
    }


    isCommonFiltersTabDisabled() {
        return isCommonFiltersTabDisabled(this.props.refinement);
    }

    handleLightboxClose = () => {
        this.setState({ lightbox: false });
    }

    render() {
        const { refinement: { minPrice, maxPrice } } = this.props;
        return (
            <div>
                <Refinement />
                <div className="sales__HeaderWrp">
                    <div className="sales__Header sales__LotsFound">
                        <h1 className="sales__LotsFoundText">
                            Lots found
                        </h1>
                        <span className="sales__HeaderCounter">
                            <span className="sales__Counter">
                                <span
                                    className="sales__CounterText js-sales-lots-found"
                                    data-test-selector="lots_found_above_table"
                                >
                                    {this.props.data.lotsFound}
                                </span>
                            </span>
                        </span>
                    </div>
                    <div className="sales__PriceRange">
                        {
                            upcoming || <PriceRangeFilter
                                minPrice={minPrice}
                                minPriceOnChange={this.props.applySearchPriceMinFilter}
                                maxPrice={maxPrice}
                                maxPriceOnChange={this.props.applySearchPriceMaxFilter}
                                disabled={this.isCommonFiltersTabDisabled()}
                                currency={this.props.currency}
                            />
                        }
                    </div>
                </div>
                <div className="sales__HeaderFilterLabel">
                    <FiltersLabel refinementSelector={refinementSelector} />
                </div>
                <div
                    className={classNames(
                        'table__SalesCatalogue',
                        { table__SalesCatalogue_results: !upcoming }
                    )}
                >
                    <CatalogueTable />
                    <Pager
                        onPageChange={this.props.onPageChange}
                        stateKey={catalogueStateKey}
                        pageSize={50}
                        pagerLength={cataloguePagerLength}
                        offsetRange={5}
                        customLabels={this.props.pagerCustomLabels}
                    />
                    <UpcomingFooterText />
                    <RelativesLightbox
                        open={this.state.lightbox}
                        title="Please sign in to edit your shortlist and notes"
                        onClose={this.handleLightboxClose}
                    />
                </div>
            </div>
        );
    }
}

/**
 * Create component properties based on app state
 *
 * @param {{}} state App state
 * @returns {{}} Properties
 */
function mapStateToProps(state) {
    const {
        refinement,
        data,
        dataTable: {
            header = [],
            meta: {
                currencies
            }
        },
        pager: {
            links
        },
    } = state[catalogueStateKey];
    const sortedColumn = header.find(column => column.sortOrder === 1);
    const isSortedByLots = sortedColumn && sortedColumn.name === 'lot_no';
    const filteredCurrencies = currencies.filter(Boolean);
    const currency = filteredCurrencies.length === 1 ? currencies[0] : null;

    return {
        refinement,
        data,
        pagerCustomLabels: isSortedByLots ? links : [],
        currency
    };
}

/**
 * Create component callable properties
 *
 * @returns {{}} Properties
 */
function mapDispatchToProps(dispatch) {
    const onAfterUpdate = updatePagerAndLotsFound(catalogueStateKey, cataloguePagerLength);

    return {
        onAfterUpdate,
        onPageChange: updateTableOnPageChange(
            dispatch,
            catalogueStateKey,
            tableConfig.url,
            onAfterUpdate
        ),
        onRowExpand({ lot_no, lot_letter }) {
            if (window.history) {
                window.history.replaceState(
                    {},
                    window.title,
                    queryUrl(window.location.href, { lot: lot_no, letter: lot_letter })
                );
            }
            dispatch(expandRow(catalogueStateKey, { lot_no, lot_letter }));
        },
        setUrl(url) {
            dispatch(setUrl(catalogueStateKey, url));
        },
        ...bindActionCreators(catalogueActions, dispatch)
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(Catalogue);



// WEBPACK FOOTER //
// ./src/js/containers/Catalogue/index.js